import React, { useReducer, useEffect } from "react";
// @ts-ignore: No TypeScript support
import makeComponentTrashable from "trashable-react";
import {
    useModal,
    useLocalNotificationBanner,
    useInterval,
    useTranslation,
} from "Shared/util/custom-hooks";
import {
    fetchErpIntegrationInvoicesSummary,
    fetchErpIntegrationInvoices,
    importErpIntegrationInvoices,
} from "Shared/util/web-api";
import {
    BrandName,
    ErpIntegrationErrorResource,
    ErpIntegrationErrorTypeResource,
    ErpIntegrationInvoiceImportInputResource,
    ErpIntegrationInvoiceResource,
    ErpIntegrationInvoiceSummaryResource,
    ErpIntegrationProviderIdResource,
    FortnoxCashMethodInvalidStockDataResource,
    WebApiError,
} from "Shared/util/types";
import useAppContext from "Contexts/app-context";
import useBrandContext from "Contexts/brand-context";
import useCustomerContext from "Contexts/customer-context";
import { formatAmount } from "Shared/util/formatters";
import { sum } from "Shared/util/calculations";
import Page from "Shared/components/page/page";
import ConfirmationModal from "Shared/components/modals/confirmation-modal";
import InformationModal from "Shared/components/modals/information-modal";
import TextLink from "Shared/components/text-link";
import InlineSvg from "Shared/components/inline-svg";
import Button from "Shared/components/button";
import InvoiceIntegrationPageTable from "./invoice-integration-page-table";
import styles from "./invoice-integration-page.css";

type State = {
    invoices: ErpIntegrationInvoiceResource[];
    loading: boolean;
    loadingCount: number;
    loadingCountDelay?: number;
    loadingMaxCount: number;
    loadingTopText: string;
    selectedInvoices: ErpIntegrationInvoiceResource[];
};

type Action = {
    payload?: any;
    type: string;
};

const reducer = (state: State, action: Action) => {
    switch (action.type) {
        case "INVOICES_FETCH_STARTED":
            return {
                ...state,
                loading: true,
                invoices: [],
                selectedInvoices: [],
                loadingTopText: action.payload.translations.format(
                    action.payload.translations.retrievingInvoicesFrom,
                    { serviceProvider: action.payload.providerId },
                ),
                loadingCount: 0,
                loadingMaxCount: 0,
            };
        case "INVOICES_SUMMARY_FETCH_FINISHED":
            return {
                ...state,
                loadingMaxCount: action.payload.loadingMaxCount,
                loadingCountDelay: action.payload.loadingCountDelay,
            };
        case "INVOICES_FETCH_FINISHED":
            return {
                ...state,
                loading: false,
                invoices: action.payload,
            };
        case "IMPORT_INVOICES_STARTED":
            return {
                ...state,
                loading: true,
                loadingTopText: action.payload.translations.format(
                    action.payload.translations.uploadInvoicesFrom,
                    { serviceProvider: action.payload.providerId },
                ),
                loadingCount: 0,
                loadingMaxCount: action.payload.numberOfSelectedInvoices,
                loadingCountDelay:
                    (1000 + action.payload.numberOfSelectedInvoices * 1400) /
                    action.payload
                        .numberOfSelectedInvoices /* Roughly estimated.. */,
            };
        case "IMPORT_INVOICES_FINISHED":
            return {
                ...state,
                loading: false,
                invoices: state.invoices.filter(
                    (x) => !state.selectedInvoices.includes(x),
                ),
                selectedInvoices: [],
                loadingCount: 0,
                loadingMaxCount: 0,
            };
        case "LOADING_COUNT_INCREASED":
            return {
                ...state,
                loadingCount: state.loadingCount + 1,
            };
        case "SELECTED_INVOICES_CHANGED":
            return {
                ...state,
                selectedInvoices: action.payload,
            };
        case "ERROR_OCCURRED":
            return {
                ...state,
                loading: false,
            };
        default:
            throw new Error("Unexpected action");
    }
};

type InvoiceIntegrationPageProps = {
    registerPromise: (p: Promise<any>) => Promise<any>;
};

const InvoiceIntegrationPage = ({
    registerPromise,
}: InvoiceIntegrationPageProps) => {
    const [state, dispatch] = useReducer(reducer, {
        invoices: [],
        loading: true,
        loadingCount: 0,
        loadingCountDelay: undefined,
        loadingMaxCount: 0,
        loadingTopText: "",
        selectedInvoices: [],
    });
    const { appData } = useAppContext();
    const { brandData } = useBrandContext();
    const { customerData, loadingCustomerData } = useCustomerContext();
    const t = useTranslation((x) => x.invoiceIntegration);
    const {
        isModalOpen: isImportModalOpen,
        openModal: openImportModal,
        closeModal: closeImportModal,
    } = useModal();
    const {
        isModalOpen: isMissingInvoicesModalOpen,
        openModal: openMissingInvoicesModal,
        closeModal: closeMissingInvoicesModal,
    } = useModal();
    const {
        NotificationBannerType,
        addLocalNotificationBanner,
        removeAllLocalNotificationBanners,
    } = useLocalNotificationBanner();

    const {
        loading,
        invoices,
        selectedInvoices,
        loadingTopText,
        loadingCount,
        loadingMaxCount,
        loadingCountDelay,
    } = state;

    const { activeErpIntegration } = customerData || {};

    useInterval(
        () => dispatch({ type: "LOADING_COUNT_INCREASED" }),
        loadingCount === loadingMaxCount ? null : loadingCountDelay,
    );

    const fetchInvoices = () => {
        if (!activeErpIntegration) return;

        dispatch({
            type: "INVOICES_FETCH_STARTED",
            payload: {
                providerId: activeErpIntegration.providerId,
                translations: t,
            },
        });

        registerPromise(
            fetchErpIntegrationInvoicesSummary(activeErpIntegration.providerId),
        )
            .then((result: ErpIntegrationInvoiceSummaryResource) => {
                dispatch({
                    type: "INVOICES_SUMMARY_FETCH_FINISHED",
                    payload: {
                        loadingCountDelay: Math.round(
                            (result.estimatedLoadTime || 0) /
                                result.numberOfInvoices,
                        ),
                        loadingMaxCount: result.numberOfInvoices,
                    },
                });
            })
            .then(() =>
                registerPromise(
                    fetchErpIntegrationInvoices(
                        activeErpIntegration.providerId,
                    ),
                ),
            )
            .then((result: ErpIntegrationInvoiceResource[]) =>
                dispatch({ type: "INVOICES_FETCH_FINISHED", payload: result }),
            )
            .catch(() => {
                addLocalNotificationBanner(
                    (tnb) =>
                        tnb.a_technical_error_occured_while_retrieving_invoices,
                    NotificationBannerType.Error,
                );
                dispatch({ type: "ERROR_OCCURRED" });
            });
    };

    useEffect(() => {
        if (loadingCustomerData) return;

        if (activeErpIntegration?.feature.invoiceImport) {
            fetchInvoices();
        } else {
            addLocalNotificationBanner(
                (tnb) => tnb.a_technical_error_occurred,
                NotificationBannerType.Error,
            );
        }
    }, [loadingCustomerData, activeErpIntegration]);

    const handleImportButtonClick = () => {
        if (!activeErpIntegration) return;

        removeAllLocalNotificationBanners();
        const selectedInvoiceIds = selectedInvoices.map(
            (i: ErpIntegrationInvoiceResource) => i.invoiceId,
        );

        dispatch({
            type: "IMPORT_INVOICES_STARTED",
            payload: {
                providerId: activeErpIntegration.providerId,
                numberOfSelectedInvoices: selectedInvoiceIds.length,
                translations: t,
            },
        });

        const payload: ErpIntegrationInvoiceImportInputResource = {
            invoiceIds: selectedInvoiceIds,
            providerId: activeErpIntegration.providerId,
        };
        registerPromise(importErpIntegrationInvoices(payload))
            .then((result) => {
                const formatMessage = (tnb: any) => {
                    const template =
                        result.numberOfInvoices > 1
                            ? tnb.sale_succeeded_multiple_invoices
                            : tnb.sale_succeeded_single_invoice;
                    return tnb.format(template, {
                        numberOfInvoices: result.numberOfInvoices,
                        totalAmount: formatAmount(result.totalAmount),
                        cultureCurrency: appData.currencyCode,
                    });
                };
                addLocalNotificationBanner(
                    formatMessage,
                    NotificationBannerType.Success,
                );
                dispatch({ type: "IMPORT_INVOICES_FINISHED" });
            })
            .catch(
                (
                    error: WebApiError<
                        ErpIntegrationErrorResource<FortnoxCashMethodInvalidStockDataResource>
                    >,
                ) => {
                    const { data: errorData, type: errorType } =
                        error?.data || {};

                    if (
                        errorType ===
                        ErpIntegrationErrorTypeResource.FortnoxCashMethodInvalidStock
                    ) {
                        addLocalNotificationBanner(
                            (tnb) =>
                                tnb.format(
                                    tnb.fortnox_cash_method_stock_error,
                                    { invoiceNumber: errorData?.invoiceNumber },
                                ),
                            NotificationBannerType.Error,
                        );
                    } else {
                        addLocalNotificationBanner(
                            (tnb) => tnb.sale_failed,
                            NotificationBannerType.Error,
                        );
                    }

                    fetchInvoices();
                },
            );
    };

    const handleFetchInvoicesButtonClick = () => {
        removeAllLocalNotificationBanners();
        fetchInvoices();
    };

    const handleSelectedRowsChanged = (
        selectedRows: ErpIntegrationInvoiceResource[],
    ) => dispatch({ type: "SELECTED_INVOICES_CHANGED", payload: selectedRows });

    const additionalContentText =
        activeErpIntegration?.providerId ===
        ErpIntegrationProviderIdResource.Fortnox
            ? t.onlyTheLast50invoicesAreDisplayes
            : "";

    return (
        <>
            <Page
                title={
                    activeErpIntegration
                        ? t.format(t.sellInvoicesFrom, {
                              serviceProvider:
                                  activeErpIntegration.providerName,
                          })
                        : ""
                }
            >
                <div className={styles.summaryOuterContainer}>
                    <div className={styles.summaryInnerContainer}>
                        <div className={styles.textContainer}>
                            <span className={styles.textTitle}>
                                {t.numberOfSelectedInvoices}
                            </span>
                            <span className={styles.textValue}>
                                {selectedInvoices.length}
                            </span>
                            <span className={styles.textValue}>
                                /{invoices.length}
                            </span>
                        </div>
                        <div className={styles.textContainer}>
                            <span className={styles.textTitle}>
                                {t.totalAmount}
                            </span>
                            <span className={styles.textValue}>
                                {formatAmount(
                                    sum(selectedInvoices, "normalizedAmount"),
                                )}{" "}
                                kr
                            </span>
                        </div>
                        <div className={styles.importButtonContainer}>
                            <Button
                                buttonType="action-primary"
                                text={t.sell}
                                onClick={openImportModal}
                                attributes={{
                                    disabled:
                                        loading || selectedInvoices.length <= 0,
                                }}
                                className={styles.importButton}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.tableToolsContainer}>
                    <TextLink
                        text={
                            <>
                                <InlineSvg src="/update.svg" />
                                {t.updateInvoices}
                            </>
                        }
                        link={handleFetchInvoicesButtonClick}
                        className={styles.updateInvoicesContainer}
                        disabled={loading}
                    />
                    <TextLink
                        text={t.areYouMissingInvoices}
                        link={openMissingInvoicesModal}
                    />
                </div>
                <InvoiceIntegrationPageTable
                    providerName={
                        activeErpIntegration?.providerName || "Undefined"
                    }
                    loading={loading}
                    loadingTopText={activeErpIntegration ? loadingTopText : ""}
                    loadingBottomText={
                        activeErpIntegration
                            ? `${loadingCount}/${loadingMaxCount}`
                            : ""
                    }
                    rows={invoices}
                    onSelectedRowsChanged={handleSelectedRowsChanged}
                    preSelectedRows={
                        brandData?.name === BrandName.PrioritetFinans
                    }
                />
            </Page>

            <ConfirmationModal
                isModalOpen={isImportModalOpen}
                closeModal={closeImportModal}
                headerText={t.sellYourInvoices}
                contentText={t.doYouWantToCompleteTheSaleOfSelectedInvoices}
                confirmText={t.sell}
                onConfirm={handleImportButtonClick}
            />
            <InformationModal
                isModalOpen={isMissingInvoicesModalOpen}
                closeModal={closeMissingInvoicesModal}
                headerText={t.format(t.areYouMissingInvoicesFrom, {
                    providerName: activeErpIntegration?.providerName,
                })}
                contentText={t.format(t.ifYouDoNotHaveInvoicesFrom, {
                    providerName: activeErpIntegration?.providerName,
                    additionalContentText: additionalContentText,
                })}
            />
        </>
    );
};

export default makeComponentTrashable(InvoiceIntegrationPage);
